'use client';

import { c as _c } from "react/compiler-runtime";
import type { AiContext } from '@/components/chat/types/ai-context';
import type { SidebarChatRequestBody } from '@/components/chat/types/chat-request';
import type { QueryClient } from '@tanstack/react-query';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { chatWarmQueryKey } from './query-keys';
import { prepareHydratedCodeContentForChatRequest } from '@/components/chat/code-content-for-chat-request';
import { prepareSidebarChatRequestBody } from '@/components/chat/types/chat-request';
type ChatWarmStatus = 'warmed' | 'ready' | 'timeout' | 'failed' | 'skipped';
type ChatWarmResult = {
  status: ChatWarmStatus;
  durationMs?: number;
};
type UseChatWarmParams = {
  aiContext: AiContext | null;
  installationId: number | null;
  pullRequestId: string | null;
  owner: string;
  repo: string;
  number: string;
  enabled?: boolean;
};
async function warmChatContext(queryClient: QueryClient, params: UseChatWarmParams): Promise<ChatWarmResult> {
  if (!params.aiContext) {
    return {
      status: 'skipped'
    };
  }

  // Rehydrate the same patchless files as a real send before applying the shared final-request
  // payload policy. This keeps prewarm and send on the same retrieval-context path.
  const requestContext = {
    ...params.aiContext,
    codeContent: params.aiContext.codeContent ? await prepareHydratedCodeContentForChatRequest(queryClient, {
      owner: params.owner,
      repo: params.repo,
      number: params.number,
      headSha: params.aiContext.headSha ?? params.aiContext.pr?.head?.sha ?? null,
      baseSha: params.aiContext.baseSha ?? params.aiContext.pr?.base?.sha ?? null
    }, params.aiContext.codeContent) : params.aiContext.codeContent
  };
  const body = prepareSidebarChatRequestBody({
    body: {
      context: requestContext,
      installationId: params.installationId,
      pullRequestId: params.pullRequestId
    } satisfies SidebarChatRequestBody
  });
  const response = await fetch('/api/chat/warm', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  });
  if (!response.ok) {
    return {
      status: 'failed'
    };
  }
  return response.json();
}

/**
 * Best-effort PR chat context prewarm.
 *
 * The sidebar should not manage its own effect/ref pair for this request:
 * React Query already gives us dedupe, a stable cache key, and no visible UI
 * error surface for this non-critical warm path.
 */
export function useChatWarm(params) {
  const $ = _c(11);
  const queryClient = useQueryClient();
  const prKey = params.pullRequestId ?? `${params.owner}/${params.repo}#${params.number}`;
  const t0 = params.aiContext?.headSha;
  const t1 = params.aiContext?.codeContent?.length;
  let t2;
  if ($[0] !== prKey || $[1] !== t0 || $[2] !== t1) {
    t2 = chatWarmQueryKey({
      prKey,
      headSha: t0,
      codeContentLength: t1
    });
    $[0] = prKey;
    $[1] = t0;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== params || $[5] !== queryClient) {
    t3 = () => warmChatContext(queryClient, params);
    $[4] = params;
    $[5] = queryClient;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = Boolean(params.enabled && params.aiContext);
  let t5;
  if ($[7] !== t2 || $[8] !== t3 || $[9] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4,
      retry: false,
      staleTime: Number.POSITIVE_INFINITY,
      gcTime: 600000,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false
    };
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return useQuery(t5);
}